import { apiService } from './api.service';
import { API_ENDPOINTS } from '../config/environment';
import { FLEET_WEBPUSH_TOKEN_KEY } from './desktopNotify.service';
import { getTokenPayload } from './auth.service';

/* Filo web paneli için FCM web push.
   Backend'de AppType.FleetWeb = 4; bu değer ASLA değişmez (kayıtlı cihaz satırları taşır).
   Web token'ı filo mobil token'ından ayrı bir Firebase web app'inde üretilir; bu yüzden
   ayrı AppType gerekir ve tek-cihaz kuralına girmez. */
export const FLEET_WEB_APP_TYPE = 4;

/* SW davranışı her değiştiğinde artırılır — tarayıcı yeni dosyayı ancak URL değişirse alır. */
export const FLEET_SW_VERSION = '2';

/* ── Kimlik kapsamlı token önbelleği ────────────────────────────────────────
   FCM token'ı tarayıcı + origin + VAPID anahtarı başına üretilir, KULLANICI başına DEĞİL.
   Aynı bilgisayarda A yöneticisi çıkıp B yöneticisi girdiğinde getToken AYNI token'ı döner.
   Önbellek yalnız token'a bakarsa kısa devre yapar, register-device hiç çağrılmaz ve
   backend'deki cihaz satırı A'nın FleetId'sine bağlı kalır: B, A'nın filo push'larını alır,
   kendi filosununkileri almaz. Bu yüzden önbellek değeri kimliği de taşır.

   Ayraç '|' bilinçli: FCM token'ları ':' içerir (örn. "cXY...:APA91b..."), GUID de içermez;
   '|' ikisinde de geçmediği için ayrım kesindir ve ayraçsız eski değerler (düz token)
   geriye dönük okunabilir. */
export const FLEET_WEBPUSH_CACHE_SEPARATOR = '|';

export function buildWebPushCacheValue(authUserId, token) {
    return `${authUserId || 'anon'}${FLEET_WEBPUSH_CACHE_SEPARATOR}${token}`;
}

/** Önbellek değerinden ham FCM token'ını çıkarır. Ayraç yoksa değer eski biçimdir (düz token). */
export function parseWebPushCacheToken(value) {
    if (!value) return null;
    const i = value.indexOf(FLEET_WEBPUSH_CACHE_SEPARATOR);
    return i >= 0 ? value.slice(i + 1) : value;
}

/** JWT'den oturum kimliği — App.jsx'in leader lock için kullandığı alanın aynısı. */
function currentAuthUserId() {
    try {
        const payload = getTokenPayload();
        return payload?.sub || payload?.nameid || null;
    } catch {
        return null;
    }
}

function clearCachedWebPushToken() {
    try {
        window.localStorage.removeItem(FLEET_WEBPUSH_TOKEN_KEY);
    } catch { /* yoksay */ }
}

let logoutCleanupInstalled = false;

/* Oturum kapanışının TÜM yollarında yerel token durumunu düşürür.
   Neden window olayı: auth.service.js buradan import EDEMEZ — webPush.service → api.service →
   auth.service zinciri zaten var, ters yön döngü olurdu. Olay veri yolu bu döngüyü kırar ve
   dinleyici index.js'ten (import grafiğinin tepesi) bir kez kurulduğu için App mount olsun
   olmasın çalışır.

   Oturum-süresi-dolması yolunda erişim token'ı ÇOKTAN silinmiştir; unregister-device isteği
   Authorization başlığı bulamayacağı için 401 alır. Bu yüzden burada YALNIZ yerel durum
   temizlenir — sunucudaki cihaz satırını asıl güvenceye alan, yukarıdaki kimlik kapsamlı
   önbellek anahtarıdır: başka bir yönetici giriş yaptığında register-device MUTLAKA çağrılır
   ve satırın FleetId'si o filoya yeniden bağlanır. */
export function installFleetWebPushLogoutCleanup() {
    if (typeof window === 'undefined' || logoutCleanupInstalled) return;
    logoutCleanupInstalled = true;
    window.addEventListener('auth:logout', clearCachedWebPushToken);
}

export function isWebPushSupported() {
    return typeof window !== 'undefined'
        && 'serviceWorker' in navigator
        && 'PushManager' in window
        && 'Notification' in window;
}

/* Vault'tan gelen istemci yapılandırması. Eksikse null → tüm akış sessizce kapanır ve
   sayfa-içi bildirim katmanı devreye girer. */
export async function fetchWebPushConfig() {
    const res = await apiService.get(API_ENDPOINTS.PUSH.WEBPUSH_CONFIG, { appType: FLEET_WEB_APP_TYPE });
    if (!res.success || !res.data) return null;

    const c = res.data;
    if (!c.apiKey || !c.projectId || !c.messagingSenderId || !c.appId || !c.vapidKey) return null;
    return c;
}

/* Filo panelinde SW için TEK kayıt noktası burasıdır (bkz. index.js — orada artık kayıt yok).
   Aynı scope'ta yalnız sorgu dizesi farklı iki register() çağrısının güncelleme semantiği
   belirsizdir: tarayıcı servis çalışanının baytlarını değişmemiş bulup güncellemeyi iptal
   edebilir, bu durumda önceden kurulu worker kendi orijinal self.location.search'ü ile
   çalışmaya devam eder — yani ?fb= konfigürasyonu hiç ulaşmayabilir. Config teslimatını
   "hangi register() önce/sonra kazanır" yarışına bırakmamak için SW'yi tek yerden, tek
   URL ile kaydederiz; config yokken bile (PWA kurulabilirlik + offline görevi sürsün diye). */
async function registerPushServiceWorker(config) {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;

    const isDev = process.env.NODE_ENV !== 'production';
    if (isDev && process.env.REACT_APP_ENABLE_SW !== 'true') return null;

    /* Config query param ile taşınır: service worker dosyası statiktir, build sırasında
       Vault değerlerini gömemeyiz. Partner panelindeki app-sw.js ile aynı yaklaşım. */
    let url = `/fleet-sw.js?v=${FLEET_SW_VERSION}`;
    if (config) {
        const fb = encodeURIComponent(JSON.stringify({
            apiKey: config.apiKey,
            projectId: config.projectId,
            messagingSenderId: config.messagingSenderId,
            appId: config.appId,
        }));
        url += `&fb=${fb}`;
    }
    if (isDev) url += '&dev=1';

    return navigator.serviceWorker.register(url, { scope: '/', updateViaCache: 'none' });
}

/* Tam kurulum: SW kaydı (config olsun olmasın, Push desteği olsun olmasın) → config yoksa dur
   → izin → token → backend kaydı. Dönen değer "web push şu an etkin mi".

   SW kaydı hem config alımının hem de Push destek kapısının ÖNÜNDEDİR: service worker'ın
   PWA kurulabilirlik ve /offline.html görevi push yapılandırmasından da PushManager
   desteğinden de bağımsızdır. Destek kapısı önce gelseydi PushManager'ı olmayan tarayıcılarda
   (sekme içi iOS Safari < 16.4) worker hiç kurulmaz, uygulama kurulabilirliğini ve çevrimdışı
   sayfasını kaybederdi. Kayıt yine TEK çağrı yerinden ve TEK URL ile yapılır. */
export async function setupFleetWebPush() {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false;

    /* Config yalnız push kurulabilecekse anlamlı; desteksiz tarayıcıda gereksiz istek atmayalım. */
    const config = isWebPushSupported() ? await fetchWebPushConfig() : null;

    let registration;
    try {
        registration = await registerPushServiceWorker(config);
    } catch {
        return false;
    }
    if (!registration) return false;

    if (!isWebPushSupported()) return false;

    if (!config) return false;

    if (Notification.permission !== 'granted') return false;

    try {
        const { initializeApp, getApps, getApp } = await import('firebase/app');
        const { getMessaging, getToken, isSupported } = await import('firebase/messaging');

        if (!(await isSupported())) return false;

        const app = getApps().length ? getApp() : initializeApp({
            apiKey: config.apiKey,
            projectId: config.projectId,
            messagingSenderId: config.messagingSenderId,
            appId: config.appId,
        });

        const token = await getToken(getMessaging(app), {
            vapidKey: config.vapidKey,
            serviceWorkerRegistration: registration,
        });

        if (!token) return false;

        /* Token VE kimlik değişmediyse backend'i yormayalım — her açılışta kayıt gereksiz.
           Kimlik değiştiyse (aynı bilgisayarda başka filo yöneticisi giriş yaptı) token aynı
           kalsa bile buradan DÜŞÜLÜR ve register-device cihaz satırının FleetId'sini yeniden
           bağlar. Bu kapı, yerel önbellek elle silinmiş olsa da unutulan bir çıkış yolu olsa da
           doğru davranışı korur. */
        const cacheValue = buildWebPushCacheValue(currentAuthUserId(), token);
        const cached = window.localStorage.getItem(FLEET_WEBPUSH_TOKEN_KEY);
        if (cached === cacheValue) return true;

        const res = await apiService.post(API_ENDPOINTS.PUSH.REGISTER_DEVICE, {
            deviceToken: token,
            appType: FLEET_WEB_APP_TYPE,
            platform: 'web',
            deviceModel: navigator.userAgent.slice(0, 120),
            appVersion: 'fleet-web',
        });

        if (!res.success) return false;

        window.localStorage.setItem(FLEET_WEBPUSH_TOKEN_KEY, cacheValue);
        return true;
    } catch {
        return false;
    }
}

/* Çıkışta token'ı düşür: aksi halde oturumu kapatan yöneticinin bilgisayarına
   filo bildirimi gitmeye devam eder. */
export async function disableWebPush() {
    let cached = null;
    try {
        cached = window.localStorage.getItem(FLEET_WEBPUSH_TOKEN_KEY);
    } catch { /* yoksay */ }
    clearCachedWebPushToken();

    /* Saklanan değer "authUserId|token" biçiminde; sunucuya HAM token gider. */
    const token = parseWebPushCacheToken(cached);
    if (!token) return;

    try {
        await apiService.post(
            `${API_ENDPOINTS.PUSH.UNREGISTER_DEVICE}?appType=${FLEET_WEB_APP_TYPE}`,
            token
        );
    } catch { /* çıkış akışı bunun için durmamalı */ }
}
